import { Ring } from '@uiball/loaders'
import React, { useRef, useState } from 'react'
import { BrowserView, isMobile, MobileView } from 'react-device-detect'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { Button } from '../../components/Button'
import { CustomText } from '../../components/CustomText'
import { BackButton } from '../../components/BackButton'
import { Loader } from '../../components/Loader'
import { Container, Content as ExContent } from '../../components/styled-components'
import Colors from '../../utils/Colors'
import { useContentDetails } from '../../hooks/useContentDetails'
import { useGroups } from '../../hooks/useGroups'
import { useUpload } from '../../hooks/useUpload'
import { useSubmitTaskAnswer } from '../../hooks/useTaskAnswer'
import { UserGroupDto } from '../../new-api/api'

const Content = styled(ExContent)<{ isLoading: boolean }>`
  display: flex;
  justify-content: ${({ isLoading }) => (isLoading ? 'center' : 'flex-start')};
  flex-direction: column;
  border-radius: 6px;
  background-color: white;
  margin-top: 50px;
  padding-top: 40px;
  padding-bottom: 40px;
`

const InnerContent = styled.div`
  margin-left: 36px;
  margin-right: 36px;
`

const UpperContent = styled.div`
  padding-top: ${isMobile ? 20 : 0}px;
  padding-left: ${isMobile ? 20 : 0}px;
  padding-right: ${isMobile ? 20 : 0}px;
`

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 24px;
  column-gap: 16px;
  ${isMobile && 'align-items: center; flex-direction: column;'}
`

const FieldLabel = styled.div`
  margin-top: 20px;
  margin-bottom: 6px;
`

const StyledSelect = styled.select`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid ${Colors.border};
  border-radius: 8px;
  font-family: Archivo;
  font-size: 14px;
  color: ${Colors.darkGray};
  background-color: ${Colors.white};
  appearance: none;
  cursor: pointer;
  outline: none;

  &:focus {
    border-color: ${Colors.primaryGreen};
  }
`

const StyledTextarea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid ${Colors.border};
  border-radius: 8px;
  font-family: Archivo;
  font-size: 14px;
  color: ${Colors.darkGray};
  background-color: ${Colors.white};
  resize: vertical;
  min-height: 100px;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: ${Colors.primaryGreen};
  }

  &::placeholder {
    color: ${Colors.gray};
  }
`

const FileInputLabel = styled.label`
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  border: 2px solid ${Colors.primaryGreen};
  border-radius: 30px;
  cursor: pointer;
  font-family: Archivo;
  font-size: 14px;
  color: ${Colors.darkGreen};
  background-color: ${Colors.white};
  margin-top: 8px;

  &:hover {
    background-color: ${Colors.lighterGray};
  }
`

const MediaGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
`

const MediaThumb = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: visible;
`

const ThumbImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
`

const VideoPlaceholder = styled.div`
  width: 80px;
  height: 80px;
  background-color: ${Colors.lighterGray};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const RemoveButton = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background-color: ${Colors.darkGray};
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  line-height: 1;
  padding: 0;
  z-index: 1;
`

const ErrorText = styled.div`
  color: ${Colors.red};
  font-size: 13px;
  margin-top: 6px;
`

type FormValues = {
  groupId: string
  answerText: string
}

type AttachedFile = {
  file: File
  previewUrl: string
}

const flattenGroups = (groups: UserGroupDto[], depth = 0): { id: number; name: string; depth: number }[] =>
  groups.flatMap((g) => [
    { id: g.id, name: g.name, depth },
    ...flattenGroups((g.children as UserGroupDto[]) || [], depth + 1),
  ])

export const TaskAnswerSubmitPage = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { t } = useTranslation()

  const { data: content, isLoading } = useContentDetails({
    contentId: Number(id),
  })
  const { data: groupsData } = useGroups()
  const { mutateAsync: uploadFile } = useUpload()
  const { mutateAsync: submitTaskAnswer, isLoading: isSubmitting } = useSubmitTaskAnswer()

  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([])
  const [submitError, setSubmitError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const answerTextEnabled = !!content?.settings?.answerText
  const answerMediaEnabled = !!content?.settings?.answerMedia

  const { register, handleSubmit, watch } = useForm<FormValues>({
    defaultValues: { groupId: '', answerText: '' },
  })

  const groupId = watch('groupId')
  const answerText = watch('answerText')

  const isSubmitDisabled =
    isSubmitting ||
    !groupId ||
    (answerTextEnabled && !answerText.trim()) ||
    (answerMediaEnabled && attachedFiles.length === 0)

  const allGroups = flattenGroups(groupsData?.groups || [])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    const newItems: AttachedFile[] = files.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }))
    setAttachedFiles((prev) => [...prev, ...newItems])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const removeFile = (index: number) => {
    setAttachedFiles((prev) => {
      URL.revokeObjectURL(prev[index].previewUrl)
      return prev.filter((_, i) => i !== index)
    })
  }

  const onSubmit = async (values: FormValues) => {
    setSubmitError(null)
    try {
      const uploadedMedias: {
        url: string
        type: string
        name: string
        size: number
      }[] = []

      for (const { file } of attachedFiles) {
        const formData = new FormData()
        formData.append('file', file)
        const response = await uploadFile(formData)
        uploadedMedias.push({
          url: response.data?.filename || '',
          type: file.type,
          name: file.name,
          size: file.size,
        })
      }

      await submitTaskAnswer({
        contentId: Number(id),
        groupId: Number(values.groupId),
        text: answerTextEnabled && values.answerText.trim() ? values.answerText.trim() : undefined,
        medias: answerMediaEnabled && uploadedMedias.length > 0 ? uploadedMedias : undefined,
      })

      navigate(`/task/${id}`)
    } catch {
      setSubmitError(t('somethingWentWrongWithTaskAnswer'))
    }
  }

  const innerContent = (
    <InnerContent>
      <UpperContent>
        <CustomText fontSize={13} lineHeight={1.4} uppercase color={Colors.gray} style={{ marginBottom: 10 }}>
          {t('TASK')}
        </CustomText>
        <CustomText fontSize={20} lineHeight={1} fontWeight={500} color={Colors.darkGray} style={{ marginBottom: 20 }}>
          {content?.name || ''}
        </CustomText>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Group select */}
          <FieldLabel>
            <CustomText fontSize={13} uppercase color={Colors.gray} lineHeight={1.4}>
              {t('sendingGroup')}
            </CustomText>
          </FieldLabel>
          <StyledSelect {...register('groupId', { required: true })}>
            <option value="">{t('chooseGroup')}</option>
            {allGroups.map((g) => (
              <option key={g.id} value={g.id}>
                {'\u00A0'.repeat(g.depth * 4)}
                {g.name}
              </option>
            ))}
          </StyledSelect>

          {/* Text answer */}
          {answerTextEnabled && (
            <>
              <FieldLabel>
                <CustomText fontSize={13} uppercase color={Colors.gray} lineHeight={1.4}>
                  {t('submittedAnswer')}
                </CustomText>
              </FieldLabel>
              <StyledTextarea placeholder={t('addText')} {...register('answerText')} />
            </>
          )}

          {/* Media upload */}
          {answerMediaEnabled && (
            <>
              <FieldLabel>
                <CustomText fontSize={13} uppercase color={Colors.gray} lineHeight={1.4}>
                  {t('imagesAndVideos')}
                  {attachedFiles.length > 0 && ` — ${t('attached', { count: attachedFiles.length })}`}
                </CustomText>
              </FieldLabel>
              <FileInputLabel>
                + {t('attachedMedia')}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
              </FileInputLabel>

              {attachedFiles.length > 0 && (
                <MediaGrid>
                  {attachedFiles.map(({ file, previewUrl }, index) => (
                    <MediaThumb key={index}>
                      {file.type.startsWith('image') ? (
                        <ThumbImage src={previewUrl} alt={file.name} />
                      ) : (
                        <VideoPlaceholder>
                          <CustomText fontSize={11} color={Colors.gray} lineHeight={1.2}>
                            {file.name.split('.').pop()?.toUpperCase()}
                          </CustomText>
                        </VideoPlaceholder>
                      )}
                      <RemoveButton type="button" onClick={() => removeFile(index)} aria-label="Remove">
                        ×
                      </RemoveButton>
                    </MediaThumb>
                  ))}
                </MediaGrid>
              )}
            </>
          )}

          {submitError && <ErrorText>{submitError}</ErrorText>}

          <ButtonContainer>
            <BackButton />
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitDisabled}
              text={t('markAsDone')}
              buttonType={isSubmitDisabled ? 'disabled' : 'primary'}
            />
          </ButtonContainer>
        </form>
      </UpperContent>
    </InnerContent>
  )

  return (
    <Container style={{ backgroundColor: isMobile ? Colors.white : Colors.background }}>
      <BrowserView renderWithFragment>
        <Content isLoading={isLoading}>{isLoading || !content ? <Loader /> : innerContent}</Content>
      </BrowserView>
      <MobileView>{isLoading || !content ? <Ring color={Colors.primaryGreen} /> : innerContent}</MobileView>
    </Container>
  )
}
